/**
 * SIM-SZENE LEIPZIG – GameOver Screen
 * Zeigt Statistiken, Auszeichnung und Neustart-Option.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { getAchievement, getGameOverMessage } from '../data/loader.js';
import { useGameState, clearGameState } from '../state/gameState.jsx';
import { playGameOver } from '../audio/sound.js';
import { shareGameOverResult } from '../utils/share.js';
import './GameOver.css';

const PATH_LABELS = { verband: 'Verband', foerderung: 'Förderung', credibility: 'Szene' };

/** Link zum Formular für Szenario-Ideen (Platzhalter bis finale URL); siehe docs/szenario-ideen-formular.md */
const SCENARIO_IDEAS_URL = '#';

export function GameOver() {
  const { state, dispatch } = useGameState();
  const contentRef = useRef(null);
  const [shareStatus, setShareStatus] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);

  useEffect(() => {
    playGameOver();
  }, []);
  const { round, gameOverCause, stats, phase, currentPathId } = state;
  
  const achievement = getAchievement(round);
  const message = getGameOverMessage(gameOverCause);
  
  // Spielzeit berechnen
  const playTime = Math.round((Date.now() - stats.startTime) / 1000 / 60);
  
  const handleRestart = () => {
    clearGameState();
    dispatch({ type: 'RESET_GAME' });
  };

  const handleShare = async () => {
    if (!contentRef.current || shareBusy) return;
    setShareBusy(true);
    setShareStatus(null);
    try {
      const result = await shareGameOverResult({
        element: contentRef.current,
        round,
        phase: phase ?? 1,
        currentPathId,
        pathLabels: PATH_LABELS,
      });
      setShareStatus(result);
      if (result !== 'unsupported') setTimeout(() => setShareStatus(null), 3000);
    } catch (e) {
      setShareStatus('unsupported');
      setTimeout(() => setShareStatus(null), 3000);
    } finally {
      setShareBusy(false);
    }
  };

  const shareFeedback = {
    shared: 'Geteilt!',
    downloaded: 'Bild gespeichert',
    copied: 'Text kopiert',
    unsupported: 'Teilen nicht möglich',
  };

  return (
    <div className="game-over">
      <div className="game-over-content" ref={contentRef}>
        {/* Titel */}
        <h1 className="game-over-title">GAME OVER</h1>
        
        {/* Todesursache */}
        <div className="game-over-cause">
          <span className="cause-icon">
            {gameOverCause === 'geld' && '💸'}
            {gameOverCause === 'nerven' && '🤯'}
            {gameOverCause === 'credibility' && '👻'}
          </span>
          <span className="cause-label">
            {gameOverCause === 'geld' && 'INSOLVENZ'}
            {gameOverCause === 'nerven' && 'BURNOUT'}
            {gameOverCause === 'credibility' && 'IRRELEVANZ'}
          </span>
        </div>
        
        {/* Nachricht */}
        <p className="game-over-message">„{message}"</p>
        
        {/* Statistiken */}
        <div className="game-over-stats">
          <div className="stat-item">
            <span className="stat-label">Überlebte Runden</span>
            <span className="stat-value">{round}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Entscheidungen</span>
            <span className="stat-value">{stats.decisionsCount}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Spielzeit</span>
            <span className="stat-value">{playTime} Min.</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Phase</span>
            <span className="stat-value">Phase {phase ?? 1}</span>
          </div>
          {currentPathId && (
            <div className="stat-item">
              <span className="stat-label">Entwicklungspfad</span>
              <span className="stat-value">{PATH_LABELS[currentPathId] ?? currentPathId}</span>
            </div>
          )}
        </div>
        
        {/* Auszeichnung */}
        {achievement && (
          <div className={`achievement achievement-${achievement.tier}`}>
            <span className="achievement-tier">
              {achievement.tier === 'bronze' && '🥉'}
              {achievement.tier === 'silver' && '🥈'}
              {achievement.tier === 'gold' && '🥇'}
              {achievement.tier === 'platinum' && '💎'}
              {achievement.tier === 'diamond' && '👑'}
              {achievement.tier.toUpperCase()}
            </span>
            <span className="achievement-title">„{achievement.title}"</span>
            <span className="achievement-desc">{achievement.description}</span>
          </div>
        )}
        
        {/* Buttons */}
        <div className="game-over-buttons">
          <button
            className="button-share"
            onClick={handleShare}
            disabled={shareBusy}
            type="button"
          >
            {shareBusy ? 'Wird erstellt…' : 'Ergebnis teilen'}
          </button>
          {shareStatus && (
            <span className="game-over-share-feedback" role="status">
              {shareFeedback[shareStatus]}
            </span>
          )}
          <button 
            className="button-restart"
            onClick={handleRestart}
          >
            Nochmal versuchen
          </button>
        </div>
        
        {/* Szenario-Ideen beitragen */}
        <div className="game-over-ideen">
          <p>
            Hast du eine Idee für ein Szenario?{' '}
            <a href={SCENARIO_IDEAS_URL} target="_blank" rel="noopener noreferrer">
              Ideen einreichen
            </a>
          </p>
        </div>
        
        {/* Spenden – mit Credits oben, Zeilenumbruch vor Vereinigungstext */}
        <div className="game-over-donate">
          <p>Ein satirisches Spiel vom <a href="https://livekommbinat.de" target="_blank" rel="noopener">LiveKommbinat Leipzig e.V.</a></p>
          <p>
            <a href="https://livekommbinat.de/das-livekommbinat-leipzig-unterstuetzen/" target="_blank" rel="noopener">
              Unterstütze das Spiel und das LiveKommbinat Leipzig e.V.
            </a>
          </p>
          <p>– Die Vereinigung Leipziger Clubs und Livemusikspielstätten. Spenden per Überweisung, Kreditkarte, SEPA und PayPal möglich.</p>
        </div>
      </div>
    </div>
  );
}
